import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api, apiErrText, getMe, myWorkInstructions, myActionableSubcontracts, countdown,
  CONTRACT_FORM_LABEL, INSTRUCTION_SEAT_LABEL, WI_KIND_LABEL, WI_STATE,
  type MeResponse, type MyActionableSubcontract, type MyInstructionItem, type WorkItem,
} from '../lib/api';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import InstructionEditor from '../components/InstructionEditor';
import { PmInstructionCard } from '../components/InstructionsPanel';

/**
 * 工程指令 / My instructions — "what do I owe on 工程指令 right now", across every job.
 *
 * The 履约/整改 queues exist for the same reason and it is worth restating: WeCom pushes are dark on prod
 * and nobody opens a package to discover that a signature is waiting, so a per-person page plus its nav
 * badge is the ONLY live signal in the building. MyFindings.tsx is the shape this follows.
 *
 * Price-free by construction: it reads `GET /me/work-instructions`, which a 项目经理 is allowed on and
 * which carries no `*_rmb` field for them at all. No amount is rendered here for anybody — the figures
 * live on the package ledger, behind the money guard.
 *
 * Grouped by the server's `reason` (signature_owed · pricing_owed · approval_owed · rework_due ·
 * daywork_to_verify · issued_by_me), with a state+role fallback for the two PR10b added, so a server
 * that has not shipped them yet still fills 待我定价 / 待我批准.
 *
 * PR10b (Eric 9/18) made this the 项目经理's ONLY working surface for 工程指令, because it is the only
 * one they can open: the package tab is `requireInternalNonPm`. Hence the ＋新建 door over
 * `GET /me/subcontracts`, and the row that expands into `PmInstructionCard` instead of linking out.
 */

type GroupKey = 'sign' | 'price' | 'approve' | 'verify' | 'rework' | 'mine';

const GROUPS: { key: GroupKey; title: string; hint: string; loud: boolean }[] = [
  { key: 'sign', title: '待我签字 / Awaiting my signature', loud: true, hint: '合同 9.1.1 — 三方签字齐全前指令不生效，也不作为结算依据。/ three signatures make it an order.' },
  { key: 'price', title: '待我定价 / Awaiting my pricing', loud: true, hint: '劳务已报价或报价已逾期失权，等我方按 4.1.2 审核。/ the contractor quoted (or let the window lapse) — audit it under 4.1.2.' },
  { key: 'approve', title: '待我批准 / Awaiting my approval', loud: true, hint: '已审核，等商务拍板：跟甲方签洽商，还是自行消化。/ priced — 商务 decides whether it is billed on or absorbed.' },
  { key: 'verify', title: '待我核实计日工 / Daywork awaiting my verification', loud: true, hint: '合同 9.2 — 2 个工作日内核实，可修改数量，原报留痕。/ verify within 2 working days; corrections are kept and audited.' },
  { key: 'rework', title: '返工确认 / Rework confirmation due', loud: true, hint: '合同 9.1.4 — 须上传完成照片，逾期不会自动通过。/ a completion photo is required; it never auto-passes.' },
  { key: 'mine', title: '我下发的（参考）/ Issued by me (context)', loud: false, hint: '不是待办，只是您开出的指令的当前状态。/ not a to-do — just where your own orders stand.' },
];

const PRICE_ROLES = ['gm', 'budget'];
const APPROVE_ROLES = ['gm', 'commercial'];

/** The states in which an order is waiting on 预算 (mirrors canPriceInstruction on the package ledger). */
function awaitingPricing(it: MyInstructionItem): boolean {
  const s = it.instruction.state;
  return s === 'quoted' || s === 'disputed' || (s === 'served' && !!it.instruction.quote_forfeited_at);
}

export default function MyInstructions() {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const isStaff = me?.account?.subject_type === 'staff';
  const role = me?.account?.role ?? '';
  const [creating, setCreating] = useState(false);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['my-work-instructions'],
    queryFn: myWorkInstructions,
    enabled: isStaff,
    refetchInterval: 60000,
  });

  if (!me?.authenticated) return <div className="card">请先登录 / please sign in.</div>;
  if (!isStaff) return <div className="card">此页仅内部员工可见 / internal staff only.</div>;

  const items = data?.items ?? [];
  const bucket: Record<GroupKey, MyInstructionItem[]> = { sign: [], price: [], approve: [], verify: [], rework: [], mine: [] };
  const claimed = new Set<string>();
  for (const it of items) {
    if (it.reason === 'signature_owed') { bucket.sign.push(it); claimed.add(it.instruction.id); }
    else if (it.reason === 'daywork_to_verify') { bucket.verify.push(it); claimed.add(it.instruction.id); }
    else if (it.reason === 'rework_due') { bucket.rework.push(it); claimed.add(it.instruction.id); }
    // PR10b: the server now emits these two directly, so they are real queue rows the badge counts.
    else if (it.reason === 'pricing_owed') { bucket.price.push(it); claimed.add(it.instruction.id); }
    else if (it.reason === 'approval_owed') { bucket.approve.push(it); claimed.add(it.instruction.id); }
  }
  // Fallback for a server that has not shipped those two reasons yet — derived from state + role, the
  // way PR8 did it. Harmless once the server sends them: `claimed` already holds those rows.
  for (const it of items) {
    if (claimed.has(it.instruction.id)) continue;
    if (PRICE_ROLES.includes(role) && awaitingPricing(it)) { bucket.price.push(it); claimed.add(it.instruction.id); }
    else if (APPROVE_ROLES.includes(role) && it.instruction.state === 'priced') { bucket.approve.push(it); claimed.add(it.instruction.id); }
  }
  for (const it of items) {
    if (!claimed.has(it.instruction.id) && it.reason === 'issued_by_me') { bucket.mine.push(it); claimed.add(it.instruction.id); }
  }

  const todo = bucket.sign.length + bucket.price.length + bucket.approve.length + bucket.verify.length + bucket.rework.length;

  return (
    <div>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
        <h2 style={{ margin: '4px 0' }}>📝 工程指令 / My work instructions</h2>
        {todo > 0 && <span className="pill pill-warn">待办 {todo}</span>}
        <span style={{ flex: 1 }} />
        <button className="btn-primary" style={{ minHeight: 44 }} onClick={() => setCreating((v) => !v)}>
          {creating ? '× 收起 / Close' : '＋ 新建工程指令 / New instruction'}
        </button>
      </div>
      <div className="small muted" style={{ marginBottom: 10 }}>
        企业微信推送在生产环境尚未开通，本页与导航徽标是目前唯一的实时提醒。/ WeCom pushes are not live on prod — this page and its nav badge are the only live reminder.
      </div>

      {creating && (
        <NewInstructionDoor
          isPm={role === 'site_pm'}
          onClose={() => setCreating(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['my-work-instructions'] })}
        />
      )}

      {isLoading && <div className="card"><Skeleton lines={4} /></div>}
      {isError && <div className="card">读取失败 / could not load: {apiErrText(error)}</div>}

      {!isLoading && !isError && items.length === 0 && (
        <div className="card">
          <EmptyState title="暂无待办 / Nothing waiting on you"
            hint="您担任签字席位、需要定价/批准、或需要核实计日工的指令会出现在这里。/ orders where you hold a seat, owe a price, owe an approval, or owe a 计日工 verification land here." />
        </div>
      )}

      {GROUPS.map((g) => bucket[g.key].length > 0 && (
        <div key={g.key} style={{ marginBottom: 16 }}>
          <div className="row-wrap" style={{ alignItems: 'center', gap: 8 }}>
            <h3 style={{ margin: '6px 0' }}>{g.title}</h3>
            <span className={`pill small ${g.loud ? 'pill-warn' : 'pill-muted'}`}>{bucket[g.key].length}</span>
          </div>
          <div className="small muted" style={{ marginBottom: 6 }}>{g.hint}</div>
          {bucket[g.key].map((it) => (
            <QueueRow key={`${g.key}-${it.instruction.id}`} it={it} me={me} isPm={role === 'site_pm'} group={g.key}
              reload={() => qc.invalidateQueries({ queryKey: ['my-work-instructions'] })} />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * ＋ 新建工程指令 — the door a 项目经理 had no way through (改改 MANUAL TEST PLAN B2).
 *
 * Eric 9/18 (工程指令): the package page is money-guarded end to end, so a site_pm could not reach the
 * 工程指令 tab to write the order the contract says THEY write (9.1.1). `GET /me/subcontracts` is the
 * price-free list of signed subcontracts they may act on — a 席位 they hold, or the standards-workflow
 * assigned PM — and it is the only fetch this door makes. PR8's editor is reused unchanged: `isPm`
 * already hides 预估金额 and strips it from the body, so no money route is ever touched here.
 *
 * `workItems` is deliberately empty. The 工程量清单 lives behind the package read, which a PM cannot
 * call; a line typed here is free text, which is exactly what a site decision looks like anyway —
 * 预算 attaches the BOQ reference when they price it.
 */
function NewInstructionDoor({ isPm, onClose, onSaved }: { isPm: boolean; onClose: () => void; onSaved: () => void }) {
  const [scId, setScId] = useState('');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['my-subcontracts'],
    queryFn: myActionableSubcontracts,
    retry: false,
  });
  const items: MyActionableSubcontract[] = data?.items ?? [];
  const chosen = items.find((s) => s.subcontract_id === scId) ?? null;
  // review 9/18 (PR10b): the BOQ only arrives with the package read, which is requireInternalNonPm — so a
  // 项目经理's lines are free text (预算 attaches the清单 reference at pricing), while 预算/商务 opening the
  // same door still get the picker they have on the package tab. Never requested for a PM: it is a 403.
  const { data: boq } = useQuery({
    queryKey: ['package-work-items', chosen?.package_id ?? ''],
    queryFn: async () => (await api<{ work_items: WorkItem[] }>(`/packages/${chosen!.package_id}`)).work_items,
    enabled: !isPm && !!chosen,
    retry: false,
  });

  const label = (s: MyActionableSubcontract) => {
    const why = s.seat ? INSTRUCTION_SEAT_LABEL[s.seat] : s.is_assigned_pm ? '指派项目经理 / assigned PM' : '';
    return `${s.package_name ?? s.package_id} · ${s.labor_co_name ?? '—'}${why ? `（${why}）` : ''}`;
  };

  return (
    <div className="card" style={{ borderColor: 'var(--accent)', marginBottom: 12 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <strong>新建工程指令 / New work instruction</strong>
        <span className="small muted">先选合同 / pick the subcontract first</span>
      </div>

      {isLoading && <Skeleton lines={2} />}
      {isError && (
        <div className="small" style={{ color: 'var(--bad)' }}>
          合同清单读取失败 / could not load your subcontracts: {apiErrText(error)}
          <div className="muted" style={{ marginTop: 4 }}>
            若您应当出现在某合同的签字席位上，请让项目上的商务/预算在「工程指令」页指派席位。/ if you should hold a seat, ask 商务/预算 to assign it on the 工程指令 tab.
          </div>
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <EmptyState title="暂无可下指令的合同 / No subcontract you can act on"
          hint="只有已签署、且您担任签字席位或为指派项目经理的分包合同才会列在这里。/ only signed subcontracts where you hold a seat (or are the assigned PM) appear here." />
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div className="row-wrap" style={{ gap: 8, alignItems: 'center' }}>
          <select value={scId} onChange={(e) => setScId(e.target.value)} style={{ minHeight: 44, minWidth: 320 }}>
            <option value="">选择合同 / pick a subcontract…</option>
            {items.map((s) => <option key={s.subcontract_id} value={s.subcontract_id}>{label(s)}</option>)}
          </select>
          {chosen?.seat && <span className="pill pill-stage small">{INSTRUCTION_SEAT_LABEL[chosen.seat]}</span>}
          {chosen && !chosen.seat && chosen.is_assigned_pm && <span className="pill pill-stage small">指派项目经理 / assigned PM</span>}
          {chosen?.project_name && <span className="small muted">{chosen.project_name}</span>}
          {chosen?.contract_form && <span className="small muted">{CONTRACT_FORM_LABEL[chosen.contract_form]}</span>}
          <button className="small" style={{ minHeight: 44 }} onClick={onClose}>× 取消 / Cancel</button>
        </div>
      )}

      {chosen && (
        <InstructionEditor
          key={chosen.subcontract_id}
          scId={chosen.subcontract_id}
          contractForm={chosen.contract_form}
          workItems={boq ?? []}
          isPm={isPm}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

/**
 * One queue row, expandable in place.
 *
 * Eric 9/18 (工程指令): PR8 shipped this row with ONE action — a link to the package's 工程指令 tab. That
 * page is `requireInternalNonPm` end to end, so a 项目经理 following it landed on a 403 and could never
 * sign seat 1 — the signature the whole 9.1.1 round waits on. The fix is to bring the card to them:
 * `PmInstructionCard` is the price-free detail view (scope, lines, 签字席位, 返工确认) the package page
 * renders for a PM anyway, and it reads nothing but `/me/work-instructions`. Non-PM roles keep the
 * link, because the full ledger with its prices really is the right place for them.
 */
function QueueRow({ it, me, isPm, reload, group }: { it: MyInstructionItem; me: MeResponse; isPm: boolean; reload: () => void; group: GroupKey }) {
  const [open, setOpen] = useState(false);
  const wi = it.instruction;
  const st = WI_STATE[wi.state];
  // 零星 orders have no quote clock at all; the rework clock is the one that matters on a rework row.
  const clock = it.reason === 'rework_due' ? countdown(wi.rework_confirm_due_at)
    : wi.kind === 'daywork' ? null
      : countdown(wi.quote_due_at);
  return (
    <div className="card" style={{ marginBottom: 8 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10 }}>
        <strong>{wi.doc_no ?? `#${wi.seq}`} · {wi.title}</strong>
        <span className={`pill ${st.pill} small`}>{st.label}</span>
        <span className="small muted">{WI_KIND_LABEL[wi.kind]} · 发生 {wi.occurred_on}</span>
        {it.seat && <span className="pill pill-stage small">{INSTRUCTION_SEAT_LABEL[it.seat]}</span>}
        {clock && clock.text !== '—' && (
          <span className="small" style={{ color: clock.expired ? 'var(--bad)' : clock.urgent ? 'var(--accent)' : undefined }}>{clock.text}</span>
        )}
        <span style={{ flex: 1 }} />
        <button className="small" style={{ minHeight: 44 }} onClick={() => setOpen((v) => !v)}>
          {open ? '收起 / Close' : group === 'sign' ? '展开签字 / Open to sign' : '展开 / Open'}
        </button>
        {/* The package ledger is requireInternalNonPm — never offer a 项目经理 a link into a 403. */}
        {!isPm && (
          <Link className="btn small" style={{ minHeight: 44 }}
            to={`/packages/${wi.package_id}?tab=instructions&wi=${wi.id}`}>台账 / Ledger ↗</Link>
        )}
      </div>
      {st.note && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>{st.note}</div>}
      {wi.reason_md && !open && <div className="small muted" style={{ marginTop: 4 }}>{wi.reason_md}</div>}
      {open && <PmInstructionCard item={it} me={me} reload={reload} />}
    </div>
  );
}
